import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { TrendingUp, TrendingDown } from 'lucide-react'

interface ChannelData {
  canal: string
  mesAnterior: number
  mesAtual: number
  crescimento: number
}

interface ChannelComparisonProps {
  data: ChannelData[]
  currentMonthLabel: string
  previousMonthLabel: string
  loading?: boolean
}

export function ChannelComparisonChart({
  data,
  currentMonthLabel,
  previousMonthLabel,
  loading
}: ChannelComparisonProps) {
  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 rounded-lg border border-gray-200 dark:border-slate-700 mt-8">
        <div className="h-80 flex items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">Carregando comparativo de canais...</p>
        </div>
      </div>
    )
  }

  const chartData = data.map(item => ({
    canal: item.canal,
    [previousMonthLabel]: item.mesAnterior,
    [currentMonthLabel]: item.mesAtual,
    crescimento: item.crescimento
  }))

  const colors = {
    [previousMonthLabel]: '#9ca3af',
    [currentMonthLabel]: '#3b82f6'
  }

  return (
    <div className="bg-white dark:bg-slate-900 p-6 rounded-lg border border-gray-200 dark:border-slate-700 mt-8">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
          Faturamento por Canal - Comparativo
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {previousMonthLabel} vs {currentMonthLabel}
        </p>
      </div>

      <ResponsiveContainer width="100%" height={350}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            className="dark:stroke-slate-700"
          />
          <XAxis
            dataKey="canal"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            className="dark:fill-gray-400"
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#6b7280' }}
            className="dark:fill-gray-400"
            tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip
            formatter={(value: any) => `R$ ${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
            contentStyle={{
              backgroundColor: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#fff'
            }}
          />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          <Bar
            dataKey={previousMonthLabel}
            fill={colors[previousMonthLabel]}
            radius={[8, 8, 0, 0]}
          />
          <Bar
            dataKey={currentMonthLabel}
            fill={colors[currentMonthLabel]}
            radius={[8, 8, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Tabela com detalhes */}
      <div className="mt-8 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
              <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Canal</th>
              <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">{previousMonthLabel}</th>
              <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">{currentMonthLabel}</th>
              <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Crescimento</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => {
              const isPositive = item.crescimento >= 0
              return (
                <tr
                  key={idx}
                  className="border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                    {item.canal}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                    R$ {item.mesAnterior.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">
                    R$ {item.mesAtual.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold flex items-center justify-end gap-1 ${
                    isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    {isPositive ? '+' : ''}{item.crescimento.toFixed(1)}%
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Resumo */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <p className="text-xs text-blue-600 dark:text-blue-300 mb-1">Canal com maior crescimento</p>
          <p className="text-lg font-bold text-blue-900 dark:text-blue-100">
            {data.reduce((max, item) => item.crescimento > max.crescimento ? item : max).canal}
          </p>
          <p className="text-sm text-blue-700 dark:text-blue-200">
            +{data.reduce((max, item) => item.crescimento > max.crescimento ? item : max).crescimento.toFixed(1)}%
          </p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
          <p className="text-xs text-red-600 dark:text-red-300 mb-1">Canal com maior queda</p>
          <p className="text-lg font-bold text-red-900 dark:text-red-100">
            {data.reduce((min, item) => item.crescimento < min.crescimento ? item : min).canal}
          </p>
          <p className="text-sm text-red-700 dark:text-red-200">
            {data.reduce((min, item) => item.crescimento < min.crescimento ? item : min).crescimento.toFixed(1)}%
          </p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
          <p className="text-xs text-purple-600 dark:text-purple-300 mb-1">Crescimento médio</p>
          <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
            {(data.reduce((sum, item) => sum + item.crescimento, 0) / data.length).toFixed(1)}%
          </p>
          <p className="text-sm text-purple-700 dark:text-purple-200">
            Todos os canais
          </p>
        </div>
      </div>
    </div>
  )
}
